import { palette } from "@minervaproject/fds";
import { useEffect, useState } from "react";

import { createBreakoutEndingSoonToast } from "components/shared/toasts";
import { useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { useTimeRemaining } from "hooks/use_time_remaining";
import { classroomSelectors } from "store";

// This would ideally be an enum for consistency, but Typescript expects string literals in enums
// and gives warnings when using the palette object.
const TimeBarColors = {
  Standard: palette.green,
  InitialWarning: palette.orange,
  EndingOrOverTime: palette.red,
};

enum SecondsRemainingWarnings {
  InitialWarning = 30,
  Ending = 10,
}

function TimeBar({
  startTime,
  endTime,
  paused = false,
  fallbackMs = null,
}: {
  startTime: Date;
  endTime: Date;
  paused?: boolean;
  fallbackMs?: number | null;
}) {
  const { timeRemainingMs: activeTimeRemaining } = useTimeRemaining(endTime);
  const timeRemainingMs = paused ? fallbackMs : activeTimeRemaining;
  const durationMilliseconds = endTime.getTime() - startTime.getTime();
  if (!timeRemainingMs) {
    return null;
  }

  // We show a full bar if we are over time.
  const timeRemainingPercentage =
    timeRemainingMs < 0 ? 100 : Math.floor((100 * timeRemainingMs) / durationMilliseconds);

  const getTimeBarColor = (timeRemainingSeconds: number) => {
    if (timeRemainingSeconds < SecondsRemainingWarnings.Ending) {
      return TimeBarColors.EndingOrOverTime;
    }
    if (timeRemainingSeconds < SecondsRemainingWarnings.InitialWarning) {
      return TimeBarColors.InitialWarning;
    }
    return TimeBarColors.Standard;
  };

  const timeColor = getTimeBarColor(timeRemainingMs / 1000);

  return (
    <div className="w-full">
      <hr
        data-testid="time-remaining-bar"
        className="m0 border-none"
        style={{ width: `${timeRemainingPercentage}%`, backgroundColor: timeColor, height: "2px" }}
      />
    </div>
  );
}

function BreakoutTimeBar({ breakoutSessionId }: { breakoutSessionId: string }) {
  const breakoutSession = useAppSelector(
    classroomSelectors.selectBreakoutSessionById(breakoutSessionId),
  );
  if (!breakoutSession.ended_on || !breakoutSession.started_on) return null;

  return (
    <TimeBar
      startTime={new Date(breakoutSession.started_on)}
      endTime={new Date(breakoutSession.ended_on)}
    />
  );
}

function ClassTimeBar() {
  const classTimer = useAppSelector(classroomSelectors.selectTimer());
  if (!classTimer.endsOn || !classTimer.durationMs) return null;

  const startTime = new Date(new Date(classTimer.endsOn).getTime() - classTimer.durationMs);
  return (
    <TimeBar
      startTime={startTime}
      endTime={new Date(new Date(classTimer.endsOn))}
      paused={!classTimer.active}
      fallbackMs={classTimer.timeRemainingWhenPausedMs}
    />
  );
}

const DISPLAY_BREAKOUT_ENDING_NOTIFICATION_AT_MS = 30 * 1000;

function BreakoutTimeRemaining({
  breakoutSessionId,
  handleOpenNotes,
}: {
  breakoutSessionId: string;
  handleOpenNotes?: () => void;
}) {
  const breakoutSession = useAppSelector(
    classroomSelectors.selectBreakoutSessionById(breakoutSessionId),
  );
  const isMobile = useIsMobile();
  const breakoutEndDate = breakoutSession.ended_on ? new Date(breakoutSession.ended_on) : null;
  const { timeRemainingMs, timeRemainingString } = useTimeRemaining(breakoutEndDate);
  const isOverTime = timeRemainingMs && timeRemainingMs < 0;
  const [showedBreakoutEndingNotification, setShowedBreakoutEndingNotification] = useState(false);

  useEffect(() => {
    if (
      handleOpenNotes &&
      timeRemainingMs &&
      timeRemainingMs <= DISPLAY_BREAKOUT_ENDING_NOTIFICATION_AT_MS &&
      !showedBreakoutEndingNotification
    ) {
      createBreakoutEndingSoonToast(handleOpenNotes);
      setShowedBreakoutEndingNotification(true);
    }
  }, [handleOpenNotes, showedBreakoutEndingNotification, timeRemainingMs]);

  if (!breakoutSession.ended_on || isOverTime) return null;

  return (
    <span className="time-remaining" data-testid="time-remaining">
      {timeRemainingString}
      {!isMobile ? " Remaining" : ""}
    </span>
  );
}

function ClassTimerTimeRemaining() {
  const classTimer = useAppSelector(classroomSelectors.selectTimer());
  const isMobile = useIsMobile();
  const timerEndDate = classTimer.endsOn ? new Date(new Date(classTimer.endsOn)) : null;
  const { timeRemainingMs: activeTimeRemaining, timeRemainingString } =
    useTimeRemaining(timerEndDate);
  const timeRemainingMs = classTimer.active
    ? activeTimeRemaining
    : classTimer.timeRemainingWhenPausedMs;
  const isOverTime = timeRemainingMs && timeRemainingMs < 0;

  if (!classTimer.endsOn || !classTimer.durationMs) return null;

  return (
    <span className="time-remaining" data-testid="time-remaining">
      {timeRemainingString}
      {isOverTime ? " over" : !isMobile ? " remaining" : ""}
    </span>
  );
}

export {
  BreakoutTimeBar,
  BreakoutTimeRemaining,
  ClassTimeBar,
  ClassTimerTimeRemaining,
  DISPLAY_BREAKOUT_ENDING_NOTIFICATION_AT_MS,
  TimeBar,
};
